import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import { systemStatusView } from "./models.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { marketDataSelectionKey } from "../../shared/api/market-data-selection.js";

export const marketDataSettingsKey = ["admin", "market-data-settings"] as const;
export type MarketDataSettings = components["schemas"]["MarketDataSettings"];
export type MarketDataSettingsInput = components["schemas"]["MarketDataSettingsInput"];
export function useMarketDataSettings() {
  const api = useApi();
  return useQuery({
    queryKey: marketDataSettingsKey,
    queryFn: async ({ signal }) =>
      requireData(await api.GET("/admin/market-data-settings", { signal })),
    refetchOnWindowFocus: false,
  });
}
export function useSaveMarketDataSettings() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: MarketDataSettingsInput) =>
      requireData(await api.PUT("/admin/market-data-settings", { body })),
    retry: false,
    onSuccess: (settings) => {
      client.setQueryData(marketDataSettingsKey, settings);
      void client.invalidateQueries({ queryKey: marketDataSelectionKey });
    },
  });
}

export const systemStatusKey = ["admin", "system-status"] as const;
export const liveScheduleStatusKey = ["admin", "live-schedules"] as const;
export const aiAnalysisSettingsKey = ["admin", "ai-analysis-settings"] as const;
export type AiAnalysisSettings = components["schemas"]["AiAnalysisSettings"];
export type AiAnalysisSettingsInput = components["schemas"]["AiAnalysisSettingsInput"];

export function useAiAnalysisSettings() {
  const api = useApi();
  return useQuery({
    queryKey: aiAnalysisSettingsKey,
    queryFn: async ({ signal }) =>
      requireData(await api.GET("/admin/ai-analysis-settings", { signal })),
    refetchOnWindowFocus: false,
  });
}

export function useSaveAiAnalysisSettings() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: AiAnalysisSettingsInput) =>
      requireData(await api.PUT("/admin/ai-analysis-settings", { body })),
    retry: false,
    onSuccess: (settings) => {
      client.setQueryData(aiAnalysisSettingsKey, settings);
      void client.invalidateQueries({ queryKey: ["ai-analysis", "status"] });
    },
  });
}

export function useLiveScheduleStatus() {
  const api = useApi();
  return useQuery({
    queryKey: liveScheduleStatusKey,
    queryFn: async ({ signal }) => requireData(await api.GET("/admin/live-schedules", { signal })),
    refetchInterval: 30_000,
  });
}

export function useSetLiveScheduleEnabled() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) =>
      requireData(await api.PUT("/admin/live-schedules", { body: { enabled } })),
    onSuccess: (status) => {
      queryClient.setQueryData(liveScheduleStatusKey, status);
    },
  });
}

export function useSystemStatus() {
  const api = useApi();
  return useQuery({
    queryKey: systemStatusKey,
    queryFn: async ({ signal }) =>
      systemStatusView(requireData(await api.GET("/admin/status", { signal }))),
    refetchInterval: 30_000,
  });
}
